// frontend/src/pages/MetaCubeOracleV5Page.jsx
import React, { useState, useEffect } from "react";
import MetaCubeOracleV5 from "../components/MetaCubeOracleV5";
import "../styles/metacube_oracle_v5.css";

export default function MetaCubeOracleV5Page() {
  // États UI
  const [particleDensityPct, setParticleDensityPct] = useState(60);
  const [chaoticSpeedPct, setChaoticSpeedPct] = useState(50);
  const [spectralIntensityPct, setSpectralIntensityPct] = useState(40);
  const [bloomIntensity, setBloomIntensity] = useState(1.2);
  const [bloomRadius, setBloomRadius] = useState(0.6);
  const [bloomThreshold, setBloomThreshold] = useState(0.0);
  const [running, setRunning] = useState(true);
  const [chaos, setChaos] = useState(false);
  const [glow, setGlow] = useState(true);

  // États particules et stats
  const [activeCount, setActiveCount] = useState(0);
  const [destroyedCount, setDestroyedCount] = useState(0);
  const [lastLightning, setLastLightning] = useState(null);

  // États entropie
  const [entropyUI, setEntropyUI] = useState(null);
  const [entropyDyn, setEntropyDyn] = useState(null);
  const [loadingUI, setLoadingUI] = useState(true);

  // Score total
  const entropyTotal =
    entropyUI !== null && entropyDyn !== null
      ? (entropyUI + entropyDyn).toFixed(4)
      : "--";

  // --- Fetch entropie UI ---
  useEffect(() => {
    const fetchEntropyUI = async () => {
      try {
        setLoadingUI(true);
        const res = await fetch("/api/geometry/metacube_oracle_v5/entropy", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            particle_density: particleDensityPct / 100,
            chaotic_speed: chaoticSpeedPct / 100,
            spectral_intensity: spectralIntensityPct / 100,
            bloom_intensity: bloomIntensity,
            bloom_radius: bloomRadius,
            bloom_threshold: bloomThreshold,
            chaos,
            glow,
            running,
          }),
        });
        const data = await res.json();
        if (data.entropy !== undefined) {
          setEntropyUI(data.entropy);
        }
      } catch {
        setEntropyUI(null);
      } finally {
        setLoadingUI(false);
      }
    };
    fetchEntropyUI();
  }, [
    particleDensityPct,
    chaoticSpeedPct,
    spectralIntensityPct,
    bloomIntensity,
    bloomRadius,
    bloomThreshold,
    chaos,
    glow,
    running,
  ]);

  // --- Reset système ---
  const resetSystem = async (newDensityPct = particleDensityPct) => {
    try {
      const res = await fetch("/api/geometry/metacube_oracle_v5/reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          particle_count: Math.floor(800 + (newDensityPct / 100) * 2000),
        }),
      });
      const data = await res.json();
      if (data.status === "reset") {
        setActiveCount(data.activeCount);
        setDestroyedCount(0);
        setEntropyDyn(0);
        setLastLightning(null);
      }
    } catch (err) {
      console.error("Reset error:", err);
    }
  };

  return (
    <div className="page-container">
      <h1>MetaCube Oracle V5</h1>

      {/* Statistiques / Jauges */}
      <div className="gauges-row">
        <div className="card small">
          <h2>Entropie UI</h2>
          <div className="value ui">
            {loadingUI ? "⏳" : entropyUI !== null ? entropyUI.toFixed(4) : "❌"}
          </div>
        </div>
        <div className="card small">
          <h2 style={{ color: "#ff0266" }}>Entropie Dynamique</h2>
          <div className="value dyn">
            {entropyDyn !== null ? entropyDyn.toFixed(4) : "⏳"}
          </div>
        </div>
        <div className="card small">
          <h2 style={{ color: "#29b6f6" }}>Score Total</h2>
          <div className="value total">{entropyTotal}</div>
        </div>
        <div className="card small">
          <h2 style={{ color: "#ffd600" }}>Particules</h2>
          <div className="value">
            ⚡ Actives: {activeCount}
            <br />
            ☠️ Détruites: {destroyedCount}
          </div>
        </div>
        <div className="card small">
          <h2 style={{ color: "#ffffff" }}>⚡ Orage Ionique</h2>
          <div className="value">
            {lastLightning ? (
              <>
                Type: {lastLightning.charge ?? "-"} <br />
                Détruites: {lastLightning.destroyed}
              </>
            ) : (
              "—"
            )}
          </div>
        </div>
      </div>

      {/* Contrôles + Visualizer */}
      <div className="controls-row">
        <div className="controls-panel">
          <button
            className="generate-button"
            onClick={() => setRunning((p) => !p)}
          >
            {running ? "⏸ Stop Animation" : "▶ Start Animation"}
          </button>
          <button
            onClick={() => setChaos((p) => !p)}
            className={
              chaos ? "toggle-button active-chaos" : "toggle-button inactive-chaos"
            }
          >
            {chaos ? "Désactiver Chaos" : "Activer Chaos"}
          </button>
          <button
            onClick={() => setGlow((p) => !p)}
            className={
              glow ? "toggle-button active-glow" : "toggle-button inactive-glow"
            }
          >
            {glow ? "Désactiver Glow" : "Activer Glow"}
          </button>

          <div className="input-group">
            <label>Densité Particules : {particleDensityPct}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={particleDensityPct}
              onChange={(e) => {
                const newVal = Number(e.target.value);
                setParticleDensityPct(newVal);
                resetSystem(newVal); // reset quand on change densité
              }}
            />
          </div>

          <div className="input-group">
            <label>Vitesse Chaotique : {chaoticSpeedPct}%</label>
            <input
              type="range"
              min={0}
              max={150}
              value={chaoticSpeedPct}
              onChange={(e) => setChaoticSpeedPct(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Intensité Spectrale : {spectralIntensityPct}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={spectralIntensityPct}
              onChange={(e) => setSpectralIntensityPct(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Halo Intensité : {bloomIntensity.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={3}
              step={0.1}
              value={bloomIntensity}
              onChange={(e) => setBloomIntensity(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Halo Rayon : {bloomRadius.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={2}
              step={0.1}
              value={bloomRadius}
              onChange={(e) => setBloomRadius(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Seuil Luminosité : {bloomThreshold.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={bloomThreshold}
              onChange={(e) => setBloomThreshold(Number(e.target.value))}
            />
          </div>

          {/* 🔄 Reset */}
          <button className="reset-button" onClick={() => resetSystem()}>
            🔄 Reset Système
          </button>
        </div>

        <MetaCubeOracleV5
          running={running}
          chaos={chaos}
          glow={glow}
          particleDensityPct={particleDensityPct}
          chaoticSpeedPct={chaoticSpeedPct}
          spectralIntensityPct={spectralIntensityPct}
          bloomIntensity={bloomIntensity}
          bloomRadius={bloomRadius}
          bloomThreshold={bloomThreshold}
          onEntropyUpdate={(val) => {
            if (val && typeof val.accumulator === "number") {
              setEntropyDyn(val.accumulator);
            }
          }}
          onStatsUpdate={(stats) => {
            setActiveCount(stats.activeCount);
            setDestroyedCount(stats.destroyedCount);
            if (stats.lightning) setLastLightning(stats.lightning);
          }}
        />
      </div>
    </div>
  );
}
